/**
 * Auth token management — pure functions with no React dependency.
 *
 * Used by both AuthContext (React) and api.ts interceptors (non-React).
 */

import type { AuthUser, UserRole } from '@/services/authService';

// ---------------------------------------------------------------------------
// Storage keys
// ---------------------------------------------------------------------------

export const TOKEN_KEY = 'askwingo_token';
export const USER_KEY = 'askwingo_user';

// ---------------------------------------------------------------------------
// Module-level token getter (for api.ts interceptor)
// ---------------------------------------------------------------------------

let _getToken: () => string | null = () => null;

export function registerTokenGetter(fn: () => string | null): void {
  _getToken = fn;
}

export function getAuthToken(): string | null {
  return _getToken();
}

// ---------------------------------------------------------------------------
// JWT helpers
// ---------------------------------------------------------------------------

export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const base64 = token.split('.')[1];
    const json = atob(base64.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') return true;
  return Date.now() / 1000 > payload.exp;
}

/**
 * The token's ``permissions`` claim (gateway ``rbac.token_permissions``: the
 * role's set plus the user's platform grants). For deciding what to OFFER in the
 * UI only; the server re-checks every permission, a platform one against a live
 * grant as well, so a claim here is never the boundary.
 */
export function tokenPermissions(token: string | null | undefined): readonly string[] {
  if (!token) return [];
  const claim = decodeJwtPayload(token)?.permissions;
  return Array.isArray(claim) ? claim.filter((p): p is string => typeof p === 'string') : [];
}

/** Exact match on the claim; a wildcard (the offline dev token's ``*``) grants nothing. */
export function tokenHasPermission(token: string | null | undefined, permission: string): boolean {
  return tokenPermissions(token).includes(permission);
}

export function userFromJwt(token: string): AuthUser | null {
  const p = decodeJwtPayload(token);
  if (!p) return null;
  return {
    user_id: (p.sub ?? p.user_id ?? '') as string,
    email: (p.email ?? '') as string,
    display_name: (p.display_name ?? p.name ?? '') as string,
    role: (p.role ?? 'member') as UserRole,
    tenant_id: (p.tenant_id ?? '') as string,
    tenant_name: (p.tenant_name ?? '') as string,
    tenant_slug: (p.tenant_slug ?? '') as string,
    password_change_required: p.password_change_required === true,
  };
}

// ---------------------------------------------------------------------------
// Trusted-browser tokens (C11 two-step sign-in)
// ---------------------------------------------------------------------------
//
// A sign-in with "trust this browser" returns an ``mfa_trust_token`` that
// waives the authenticator code (never the password) for 30 days. It is kept
// per account (tenant slug + email), so one person's token is never sent on
// another's sign-in from the same browser. A session that ends on its own
// (it expired, its refresh failed) keeps it, so everyday sign-in stays
// password-only; an explicit Sign out removes the signing-out account's, for
// every tab, since the store is shared (CASA 6.6.1, clearTrustTokenOnSignOut).
// The gateway also ends it on a password change or a factor reset. Storage
// can throw (private mode, blocked site data); every access is guarded, and a
// failure only means the code is asked for again.

export const TRUST_TOKENS_KEY = 'askwingo_mfa_trust';

function trustAccountKey(tenantSlug: string, email: string): string {
  return `${tenantSlug.trim().toLowerCase()}:${email.trim().toLowerCase()}`;
}

function readTrustTokens(): Record<string, string> {
  try {
    const raw = localStorage.getItem(TRUST_TOKENS_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, string>)
      : {};
  } catch {
    return {};
  }
}

function writeTrustTokens(tokens: Record<string, string>): void {
  try {
    if (Object.keys(tokens).length === 0) localStorage.removeItem(TRUST_TOKENS_KEY);
    else localStorage.setItem(TRUST_TOKENS_KEY, JSON.stringify(tokens));
  } catch {
    /* storage unavailable: the next sign-in asks for a code, nothing breaks */
  }
}

export function getTrustToken(tenantSlug: string, email: string): string | null {
  const token = readTrustTokens()[trustAccountKey(tenantSlug, email)];
  return typeof token === 'string' && token ? token : null;
}

/**
 * The saved trust token for a sign-in that names no workspace (XC-TEAM-INVITES T5): the one saved
 * for this email, when exactly one workspace holds one; with several (or none) nothing is sent and
 * the code is asked for, never a guess at which account the password will pick.
 */
export function findTrustTokenForEmail(email: string): { slug: string; token: string } | null {
  const suffix = `:${email.trim().toLowerCase()}`;
  const found = Object.entries(readTrustTokens()).filter(
    ([key, token]) => key.endsWith(suffix) && typeof token === 'string' && token,
  );
  if (found.length !== 1) return null;
  const [key, token] = found[0];
  return { slug: key.slice(0, key.length - suffix.length), token };
}

export function setTrustToken(tenantSlug: string, email: string, token: string): void {
  writeTrustTokens({ ...readTrustTokens(), [trustAccountKey(tenantSlug, email)]: token });
}

export function clearTrustToken(tenantSlug: string, email: string): void {
  const tokens = readTrustTokens();
  if (!(trustAccountKey(tenantSlug, email) in tokens)) return;
  delete tokens[trustAccountKey(tenantSlug, email)];
  writeTrustTokens(tokens);
}

/**
 * An explicit Sign out ends this browser's trust for the account signing out
 * (CASA 6.6.1); other accounts' tokens stay. With the tenant unknown (a
 * session restored without its saved user), that email's token goes under
 * every tenant.
 */
export function clearTrustTokenOnSignOut(
  tenantSlug: string | null | undefined,
  email: string | null | undefined,
): void {
  const who = (email ?? '').trim().toLowerCase();
  if (!who) return;
  if (tenantSlug?.trim()) {
    clearTrustToken(tenantSlug, who);
    return;
  }
  const tokens = readTrustTokens();
  const kept = Object.fromEntries(Object.entries(tokens).filter(([key]) => !key.endsWith(`:${who}`)));
  if (Object.keys(kept).length < Object.keys(tokens).length) writeTrustTokens(kept);
}
